/* eslint-disable @typescript-eslint/no-explicit-any */

import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { isEqual, keys, map } from 'es-toolkit/compat';
import { noop } from 'es-toolkit';

import clsx from 'clsx';

import * as globalEventsHelper from '../../../utils/globalEventsHelper';
import BaseComponent from '../../BaseComponent';
import BasicDropdownOptionWrapper from './BasicDropdownOptionWrapper';
import BasicDropdownTrigger from './BasicDropdownTrigger';
import InputContainer from '../../forms/InputContainer';
import ScrollLockComponent from '../../ScrollLockComponent';

interface OptionProps<T> {
  isSelected?: boolean;
  option: T;
  onSelect?(event: React.MouseEvent<HTMLElement> | undefined, value: T): void;
}
interface Props<T> {
  additionalDropdownWidth?: number;
  component:
    | React.ComponentClass<OptionProps<T>>
    | React.FC<React.PropsWithChildren<OptionProps<T>>>;
  dropdownTestId?: string;
  dropdownTriggerClassName?: string;
  contextClassName?: string;
  defaultPlaceholder?: string;
  disabled?: boolean;
  hideChevron?: boolean;
  labelId?: string;
  options: T[];
  position?: 'bottom' | 'top';
  selectedOption?: T;
  size?: 'small' | 'normal';
  startsOpen?: boolean;
  onSelect(value: T): void;
  isOpen?: boolean;
  onOpen?(isOpen: boolean): void;
  id?: string;
}

interface State {
  isOpen: boolean;
}

/**
 * @deprecated Prefer a Pantry component instead. See https://docs.tocktix.com/pantry.
 * If an equivalent component doesn't exist yet, reach out in #pantry-design-system on Slack.
 */
export default class BasicDropdown<T = SelectOption> extends BaseComponent<Props<T>, State> {
  static defaultProps: Partial<Props<any>> = {
    defaultPlaceholder: 'Select an option',
    disabled: false,
    position: 'bottom',
    size: 'normal',
    startsOpen: false,
  };

  scrollContextNode: any;
  selectedNode: any;

  constructor(props: Props<T>) {
    super(props);

    this.state = {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      isOpen: props.startsOpen!,
    };
  }

  static getDerivedStateFromProps(props: Props<any>, state: State) {
    if (props.isOpen == null) {
      return state;
    }

    return {
      isOpen: props.isOpen,
    };
  }

  componentDidMount() {
    window.addEventListener('resize', this.handleReposition);
    window.addEventListener('orientationchange', this.handleReposition);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleReposition);
    window.removeEventListener('orientationchange', this.handleReposition);
  }

  componentDidUpdate(prevProps: Props<T>, prevState: State) {
    if (
      ((!prevState.isOpen && this.state.isOpen) ||
        prevProps.selectedOption !== this.props.selectedOption) &&
      this.scrollContextNode
    ) {
      this.scrollContextNode.scrollTo(this.selectedNode);
      this.handleReposition();
    }
  }

  /*
    This function dynamically sets the left, top, and width of the dropdown
    options relative to the trigger. Right now the options are rendered
    "in place" so this *could* be accomplished with CSS classes, but eventually
    we'd like to render the options within a portal. This would allow us to
    avoid z-index issues and dynamically position the options so they're always
    visible on screen.
  */
  handleReposition() {
    if (!this.state.isOpen) {
      return;
    }

    const trigger = ReactDOM.findDOMNode((this.refs as any).trigger) as any;
    const options = ReactDOM.findDOMNode((this.refs as any).options) as any;

    const {
      offsetLeft: triggerLeft,
      offsetTop: triggerTop,
      offsetWidth: triggerWidth,
      offsetHeight: triggerHeight,
    } = trigger;

    const { offsetHeight: optionsHeight } = options;

    // The dropdown should always be slightly wider than the trigger so it feels
    // separate and above the rest of the page.
    const dropdownWidthBuffer = 6;
    const dropdownLeft = triggerLeft - dropdownWidthBuffer;
    const dropdownWidth =
      triggerWidth + dropdownWidthBuffer * 2 + (this.props.additionalDropdownWidth || 0);

    let dropdownTop: any;
    const margin = 6;

    if (this.props.position === 'bottom') {
      dropdownTop = triggerTop + triggerHeight + margin;
    } else {
      dropdownTop = triggerTop - optionsHeight - margin;
    }

    const style: any = {
      left: `${dropdownLeft}px`,
      top: `${dropdownTop}px`,
      width: `${dropdownWidth}px`,
    };

    keys(style).forEach((key: any) => (options.style[key] = style[key]));
  }

  handleClose() {
    if (!this.state.isOpen) {
      return;
    }

    globalEventsHelper.removeGlobalClickEvent(this.handleClose, 'popover');

    this.setState({
      isOpen: false,
    });
    if (this.props.onOpen) {
      this.props.onOpen(false);
    }
  }

  handleOpen() {
    if (this.state.isOpen) {
      return;
    }

    // Multiple globalEventsHelper.addGlobalClickEvent are triggering each others handleClose methods. See https://tock.atlassian.net/browse/TK-15154
    setTimeout(() => {
      globalEventsHelper.addGlobalClickEvent(this.handleClose, 'popover');
    }, 0);

    this.setState({
      isOpen: true,
    });
    if (this.props.onOpen) {
      this.props.onOpen(true);
    }
  }

  handleToggle() {
    if (this.state.isOpen) {
      this.handleClose();
    } else {
      this.handleOpen();
    }
  }

  handleSelect(event: React.MouseEvent<HTMLElement> | undefined, option: T) {
    this.props.onSelect(option);
    if (event) {
      this.handleClose();
    }
  }

  handleSetScrollContextNode(node: ScrollLockComponent) {
    this.scrollContextNode = node;
  }

  handleSetSelectedNode(node: BasicDropdownOptionWrapper | null) {
    this.selectedNode = node;
  }

  render() {
    const {
      component,
      contextClassName,
      defaultPlaceholder,
      disabled,
      dropdownTestId,
      dropdownTriggerClassName,
      labelId,
      hideChevron,
      options,
      selectedOption,
      size,
    } = this.props;

    const { isOpen } = this.state;

    const className = clsx(
      'Select',
      'Select--strip',
      'Select--minimal',
      'BasicDropdown',
      {
        'is-open': isOpen,
        'Select--skinny': size === 'small',
      },
      contextClassName
    );

    return (
      <InputContainer className={className} disabled={disabled}>
        <BasicDropdownTrigger
          isOpen={isOpen}
          className={dropdownTriggerClassName}
          disabled={disabled}
          ref="trigger"
          onToggle={this.handleToggle}
          hideChevron={hideChevron}
          labelId={labelId}
          data-testid={`${dropdownTestId ?? 'basicdropdown'}-trigger`}
        >
          {React.createElement(component, {
            option:
              selectedOption ||
              ({
                value: null,
                label: defaultPlaceholder,
              } as any),
          })}
        </BasicDropdownTrigger>

        {isOpen && (
          <div
            ref="options"
            className={clsx('BasicDropdown-options', {
              'is-top': this.props.position !== 'bottom',
              'is-bottom': this.props.position === 'bottom',
            })}
            aria-labelledby={labelId}
            data-testid={dropdownTestId}
          >
            <ScrollLockComponent
              className="BasicDropdown-scrollContext"
              ref={this.handleSetScrollContextNode}
            >
              {map(options, (option, index: number) => {
                const key = `dropdown-option-${index}`;
                const isSelected = isEqual(option, selectedOption);
                const ref = isSelected ? this.handleSetSelectedNode : noop;

                return (
                  <BasicDropdownOptionWrapper
                    component={component}
                    isSelected={isSelected}
                    key={key}
                    onClick={this.handleSelect}
                    option={option}
                    ref={ref}
                    data-testid={`${dropdownTestId ?? 'basicdropdown'}-option`}
                  />
                );
              })}
            </ScrollLockComponent>
          </div>
        )}
      </InputContainer>
    );
  }
}
